import { useMemo, useState } from 'react'

import cn from 'classnames'

import ChainToken from '@src/components/ChainToken'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import CloseIcon from '@src/components/icons/Close'
import ProcessFailedIcon from '@src/components/icons/ProcessFailed'
import { StepNames } from '@src/enums'
import { LiquiditySource } from '@src/enums'
import SwapModalFooter from '@src/pages/Exchange/components/SwapInProgressFlow/SwapModalFooter'
import TransactionDetails from '@src/pages/Exchange/components/SwapInProgressFlow/TransactionDetails'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { createContextLogger } from '@src/utils/logger'
import { useConfigStore } from '@src/zustand_stores'

const logger = createContextLogger('SwapFailed')

// Jupiter error code mapping
const JUPITER_ERROR_CODES: Record<string, { name: string; message: string }> = {
    '6001': {
        name: 'SlippageToleranceExceeded',
        message: 'Slippage tolerance exceeded. Try increasing your slippage tolerance or use dynamic slippage.',
    },
    '6008': {
        name: 'NotEnoughAccountKeys',
        message: 'Transaction is missing required account keys. Please try again with a new quote.',
    },
    '6014': {
        name: 'IncorrectTokenProgramID',
        message: 'Incompatible token program. This may occur with Token2022 tokens.',
    },
    '6017': {
        name: 'ExactOutAmountNotMatched',
        message: 'Output amount does not match expected value. Try increasing your slippage tolerance.',
    },
}

/**
 * Parse Jupiter error message from transaction result
 * @param errorMessage The raw error message from the transaction
 * @returns Formatted user-friendly error message
 */
const parseJupiterError = (errorMessage?: string): string => {
    if (!errorMessage) return 'Unknown error occurred during swap'

    try {
        // Check if this is a Jupiter custom error code
        if (errorMessage.includes('InstructionError') && errorMessage.includes('Custom')) {
            // Extract the custom error code
            const match = errorMessage.match(/Custom":([0-9]+)/)
            if (match && match[1]) {
                const errorCode = match[1]
                logger.info('Found Jupiter custom error code:', { errorCode })

                // Look up the error in our mapping
                if (JUPITER_ERROR_CODES[errorCode]) {
                    return JUPITER_ERROR_CODES[errorCode].message
                }

                return `Jupiter swap failed with error code ${errorCode}. Please try again.`
            }
        }

        // If we can't parse a specific error code, return the original message
        return errorMessage
    } catch (error) {
        logger.error('Error parsing Jupiter error message:', error)
        return errorMessage || 'Unknown error occurred during swap'
    }
}

interface ISwapFailedProps {
    sellData: IExchangeData
    buyData: IExchangeData
    result?: {
        sourceTxHash?: string
        message?: string
    }
    currentStep: StepNames
    fees: IFee[]
    destinationAddress?: string
    liquiditySource: LiquiditySource
    onClose?: () => void
}

export default function SwapFailed({
    sellData,
    buyData,
    result = undefined,
    currentStep,
    fees,
    destinationAddress,
    liquiditySource,
    onClose,
}: ISwapFailedProps) {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const [showDetails, setShowDetails] = useState(true)
    const transactionHash = useMemo(() => {
        if (result?.sourceTxHash) {
            const urlParts = result.sourceTxHash.split('/')
            return urlParts[urlParts.length - 1]
        }
        return ''
    }, [result?.sourceTxHash])

    return (
        <div className='space-y-10 px-4'>
            <div className='flex w-full justify-center'>
                <p className='text-base font-medium text-[#E84142]'>Swap Failed</p>
            </div>
            <div className='relative flex h-[212px] items-center justify-between rounded-3xl border border-[#1A1B20] px-8'>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={sellData.token?.logoURI}
                        networkIcon={availableNetworks[sellData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
                <div className='absolute left-1/2 top-0 flex h-full -translate-x-1/2 items-center justify-between gap-2'>
                    <ProcessFailedIcon />
                    <div className='flex h-5 w-5 items-center justify-center rounded-full bg-[#16171A]'>
                        <CloseIcon className='text-[#E84142]' />
                    </div>
                    <ProcessFailedIcon />
                </div>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={buyData.token?.logoURI}
                        networkIcon={availableNetworks[buyData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
            </div>
            <div className='space-y-6'>
                {result?.message && (
                    <div className='flex justify-center'>
                        <span className='text-sm text-mach-sub-text'>
                            {liquiditySource === LiquiditySource.jupiter
                                ? parseJupiterError(result.message)
                                : result.message}
                        </span>
                    </div>
                )}
                <div className='flex items-center gap-4'>
                    <div className='flex-grow border border-[#313339]'></div>
                    <div
                        className='flex cursor-pointer items-center gap-1'
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        <span className='text-sm text-mach-accent-forgeround'>Details</span>
                        <ChevronDownIcon className={cn('h-4 w-4 transition-all', !showDetails ? 'rotate-180' : '')} />
                    </div>
                    <div className='flex-grow border border-[#313339]'></div>
                </div>
                {
                    <div
                        className={cn(
                            'flex flex-col overflow-hidden transition-all duration-500',
                            showDetails ? (transactionHash ? 'h-[284px]' : 'h-[242px]') : 'h-0'
                        )}
                    >
                        <div className='space-y-6'>
                            <TransactionDetails
                                sellData={sellData}
                                buyData={buyData}
                                fees={fees}
                                destinationAddress={destinationAddress}
                                transactionHash={transactionHash}
                                currentStep={currentStep}
                                liquiditySource={liquiditySource}
                                // do not show this here (dup info w/ the rest of the flow)
                                isCCTPTransfer={false}
                            />
                        </div>
                    </div>
                }
                {result?.sourceTxHash && <SwapModalFooter transactionHash={result.sourceTxHash} onClose={onClose} />}
            </div>
        </div>
    )
}
